<template>
  <v-app-bar
    app
    flat
    height="72"
    class="app-navbar"
    :class="{
      dark: isDark,
      'navbar-rtl': isArabic,
      'navbar-ltr': !isArabic,
    }"
    :dir="isArabic ? 'rtl' : 'ltr'"
  >
    <v-app-bar-nav-icon class="menu-btn" @click="layout.toggleDrawer()" />

    <div class="logos-box">
      <img src="@/assets/yemen-logo.png" :alt="$t('navbar.yemenLogo')" />
    </div>

    <div class="title-box">
      <div class="main-title">
        {{ $t("navbar.systemTitle") }}
      </div>
      <div class="sub-title">
        {{ $t("navbar.systemSubtitle") }}
      </div>
    </div>

    <v-spacer />

    <!-- <v-btn class="stage-btn" variant="flat">
      {{ $t("navbar.pilotStage") }}
    </v-btn> -->

    <v-btn
      class="top-btn language-btn"
      variant="outlined"
      @click="toggleLanguage"
    >
      <v-icon size="18">mdi-translate</v-icon>
      {{ isArabic ? "العربية" : "English" }}
    </v-btn>

    <v-menu :location="isArabic ? 'bottom start' : 'bottom end'" offset="8">
      <template #activator="{ props }">
        <v-btn v-bind="props" class="user-btn" variant="flat">
          <v-icon size="22">mdi-account-circle</v-icon>
          <span class="user-name">{{ userName }}</span>
          <v-icon size="18">mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <v-list
        class="user-menu"
        density="comfortable"
        :dir="isArabic ? 'rtl' : 'ltr'"
      >
        <v-list-item to="/profile" class="profile-item" rounded="lg">
          <template #prepend>
            <v-icon color="#173f6b">mdi-account-outline</v-icon>
          </template>

          <v-list-item-title class="font-weight-bold">
            {{ userName }}
          </v-list-item-title>

          <v-list-item-subtitle>
            {{ roleName }}
          </v-list-item-subtitle>
        </v-list-item>

        <v-divider class="my-2" />

        <v-list-item class="logout-item" rounded="lg" @click="logout">
          <template #prepend>
            <v-icon color="#c62828">mdi-logout</v-icon>
          </template>

          <v-list-item-title>
            {{ $t("navbar.logout") }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useLayoutStore } from "@/stores/layout";

const router = useRouter();
const layout = useLayoutStore();
const { locale, t } = useI18n();

const isDark = ref(false);
const currentUser = ref({});
const currentRole = ref(null);

const isArabic = computed(() => locale.value === "ar");

const roleLabels = {
  admin: { ar: "مدير النظام", en: "Administrator" },
  administrator: { ar: "مدير النظام", en: "Administrator" },
  engineer: { ar: "مهندس", en: "Engineer" },
  hr: { ar: "الموارد البشرية", en: "Human Resources" },
  services: { ar: "إدارة الخدمات", en: "Services Management" },
  service_management: { ar: "إدارة الخدمات", en: "Services Management" },
  governance: { ar: "الحوكمة", en: "Governance" },
};

const getStoredValue = (key) => {
  const value = localStorage.getItem(key);
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const loadAuthenticatedUser = () => {
  const storedUser = getStoredValue("user");

  currentUser.value =
    storedUser && typeof storedUser === "object" ? storedUser : {};
  currentRole.value = getStoredValue("role") || currentUser.value.role || null;
};

const userName = computed(() => {
  const user = currentUser.value;

  if (isArabic.value) {
    return (
      user.name_ar ||
      user.name ||
      user.name_en ||
      user.full_name ||
      user.username ||
      t("navbar.user")
    );
  }

  return (
    user.name_en ||
    user.name ||
    user.name_ar ||
    user.full_name ||
    user.username ||
    t("navbar.user")
  );
});

const roleName = computed(() => {
  const role = currentRole.value;
  const language = isArabic.value ? "ar" : "en";

  if (!role) {
    return isArabic.value ? "دور غير محدد" : "No role";
  }

  if (typeof role === "string") {
    return roleLabels[role.toLowerCase()]?.[language] || role;
  }

  const translatedName = isArabic.value
    ? role.name_ar || role.display_name_ar || role.label_ar
    : role.name_en || role.display_name_en || role.label_en;

  if (translatedName) return translatedName;

  const roleCode = role.slug || role.code || role.name;

  if (roleCode) {
    return roleLabels[String(roleCode).toLowerCase()]?.[language] || roleCode;
  }

  return isArabic.value ? "دور غير محدد" : "No role";
});

const applyLanguage = () => {
  const currentLanguage = locale.value;

  document.documentElement.lang = currentLanguage;
  document.documentElement.dir = currentLanguage === "ar" ? "rtl" : "ltr";
  document.body.dir = currentLanguage === "ar" ? "rtl" : "ltr";

  localStorage.setItem("lang", currentLanguage);
};

const toggleLanguage = () => {
  locale.value = locale.value === "ar" ? "en" : "ar";
  applyLanguage();
};

const applyTheme = () => {
  const theme = isDark.value ? "dark" : "light";

  document.documentElement.setAttribute("data-theme", theme);
  document.body.classList.toggle("dark-mode", isDark.value);
  localStorage.setItem("theme", theme);
};

const logout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("role");
  localStorage.removeItem("permissions");
  localStorage.removeItem("isAuthenticated");

  currentUser.value = {};
  currentRole.value = null;

  router.replace("/login");
};

watch(locale, applyLanguage);

onMounted(() => {
  loadAuthenticatedUser();

  isDark.value = localStorage.getItem("theme") === "dark";

  const savedLanguage = localStorage.getItem("lang");
  if (savedLanguage === "ar" || savedLanguage === "en") {
    locale.value = savedLanguage;
  }

  applyTheme();
  applyLanguage();
});
</script>

<style scoped>
.app-navbar {
  background: linear-gradient(270deg, #0d2240, #15355c 55%, #1c4373) !important;
  color: white !important;
  padding: 0 18px !important;
}

.app-navbar.dark {
  background: linear-gradient(270deg, #050b14, #0d2240 55%, #15355c) !important;
}

.navbar-rtl {
  direction: rtl;
}

.navbar-rtl .title-box {
  text-align: right;
}

.navbar-ltr {
  direction: ltr;
}

.navbar-ltr .title-box {
  text-align: left;
}

.menu-btn {
  width: 48px !important;
  height: 48px !important;
  border-radius: 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  color: white !important;
}

.navbar-rtl .menu-btn {
  margin-left: 14px !important;
  margin-right: 0 !important;
}

.navbar-ltr .menu-btn {
  margin-right: 14px !important;
  margin-left: 0 !important;
}

.logos-box {
  height: 46px;
  background: white;
  border-radius: 7px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.navbar-rtl .logos-box {
  margin-left: 14px;
  margin-right: 0;
}

.navbar-ltr .logos-box {
  margin-right: 14px;
  margin-left: 0;
}

.logos-box img {
  height: 38px;
  object-fit: contain;
}

.title-box {
  line-height: 1.3;
  min-width: 0;
}

.main-title {
  font-size: 17px;
  font-weight: 800;
  white-space: nowrap;
  color: white !important;
}

.sub-title {
  font-size: 12px;
  color: #bbd1e8 !important;
  white-space: nowrap;
}

.stage-btn {
  background: #b8923e !important;
  color: #2a1f08 !important;
  border-radius: 999px !important;
  height: 34px !important;
  padding: 0 16px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
}

.navbar-rtl .stage-btn,
.navbar-rtl .top-btn {
  margin-left: 10px !important;
  margin-right: 0 !important;
}

.navbar-ltr .stage-btn,
.navbar-ltr .top-btn {
  margin-right: 10px !important;
  margin-left: 0 !important;
}

.top-btn {
  height: 38px !important;
  border-radius: 7px !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
  font-size: 13px !important;
  gap: 6px;
}

.language-btn {
  min-width: 102px !important;
}

.user-btn {
  height: 40px !important;
  border-radius: 999px !important;
  background: rgba(255, 255, 255, 0.12) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.22) !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  padding: 0 12px !important;
  gap: 6px;
}

.user-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.user-name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-menu {
  min-width: 250px;
  border-radius: 14px !important;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.18) !important;
}

.profile-item:hover {
  background: #eef5fc !important;
}

.logout-item {
  color: #c62828 !important;
  font-weight: 700;
}

.logout-item:hover {
  background: #fff1f1 !important;
}

@media (max-width: 1050px) {
  .stage-btn {
    display: none !important;
  }
}

@media (max-width: 900px) {
  .main-title {
    font-size: 13px;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sub-title {
    font-size: 10px;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-name {
    display: none;
  }
}

@media (max-width: 700px) {
  .top-btn:not(.language-btn) {
    display: none !important;
  }

  .language-btn {
    min-width: 46px !important;
    padding: 0 8px !important;
    font-size: 0 !important;
  }

  .language-btn :deep(.v-icon) {
    font-size: 20px !important;
  }
}

@media (max-width: 640px) {
  .logos-box {
    display: none;
  }

  .sub-title {
    display: none;
  }
}
</style>
